import {
  ContentType,
  COURSE_ENTITY_INDEX_TYPES,
  COURSE_VISIBILITY,
  CourseEntityIndexViewObject
} from "@seneca/course-service-types";

import { Dispatch, GetState } from "common/types";

import { getClient } from "services";
import errorHandler from "services/courseService/errorHandler";

import {
  fetchEnrolledCoursesIfNotFetched,
  saveNewCourseEnrollmentLocallyIfNotAlreadyEnrolled
} from "seneca-common/features/enrollments/features/course-enrollments/service/actions";
import { isSignedIn } from "seneca-common/features/user/state";
import { awaitCondition } from "seneca-common/utils/state/await-condition";

import {
  addCourse,
  addCourseSkeleton,
  addSection,
  courseDetailsFetchFinished,
  courseDetailsNeedFetching,
  courseSectionsNeedFetching,
  fetchCourseActionFactory,
  fetchCourseDetailsActionFactory,
  fetchCourseSectionsActionFactory,
  fetchSectionActionFactory,
  fullCourseNeedFetching,
  receiveCoursesDetails,
  sectionNeedsFetching
} from "../state";

const getCourseServiceClient = () => getClient("courseService");

// Course details:
export const fetchCoursesDetails = fetchCourseDetailsActionFactory({
  requestFunction: (options = {}) =>
    getCourseServiceClient().fetchCourseDetails({
      ...options,
      noRegion: true,
      maxVisibility: COURSE_VISIBILITY.hidden
    }),
  storeAction: receiveCoursesDetails
});

export function fetchCoursesDetailsIfNotFetched() {
  return function (dispatch: Dispatch, getState: GetState) {
    if (courseDetailsNeedFetching(getState())) {
      return dispatch(fetchCoursesDetails());
    } else {
      return dispatch(awaitCondition(courseDetailsFetchFinished));
    }
  };
}

// Full courses:
const fetchCourseAction = fetchCourseActionFactory({
  requestFunction: (courseId: string) =>
    getCourseServiceClient().fetchCourse(courseId),
  storeAction: addCourse,
  errorHandler
});

export const fetchCourse = (courseId: string) => {
  return async (dispatch: Dispatch) => {
    const course = await dispatch(fetchCourseAction(courseId, courseId));

    if (course && !(course instanceof Error)) {
      dispatch(saveNewCourseEnrollmentLocallyIfNotAlreadyEnrolled(courseId));
    }

    return course;
  };
};

export const fetchCourseIfNotFetched =
  (courseId: string) => async (dispatch: Dispatch, getState: GetState) => {
    if (fullCourseNeedFetching(getState(), courseId)) {
      return dispatch(fetchCourse(courseId));
    }
  };

// Course section details:
const fetchCourseSectionsAction = fetchCourseSectionsActionFactory({
  requestFunction: (courseId, options) =>
    getCourseServiceClient().fetchCourseSectionDetails(courseId, options),
  storeAction: addCourseSkeleton,
  errorHandler
});

export const fetchCourseSections = (courseId: string) => {
  return async (dispatch: Dispatch) => {
    return await dispatch(fetchCourseSectionsAction(courseId, courseId));
  };
};

export const fetchCourseSectionsIfNotFetched =
  (courseId: string) => async (dispatch: Dispatch, getState: GetState) => {
    if (courseSectionsNeedFetching(getState(), courseId)) {
      return dispatch(fetchCourseSections(courseId));
    }
  };

// Section:
const fetchSectionAction = fetchSectionActionFactory({
  requestFunction: (courseId, sectionId, contentTypes) =>
    getCourseServiceClient().fetchSection(courseId, sectionId, contentTypes),
  storeAction: addSection,
  errorHandler
});

export const fetchSection = (
  courseId: string,
  sectionId: string,
  contentTypes?: Array<ContentType>
) => {
  return async (dispatch: Dispatch, getState: GetState) => {
    return dispatch(
      fetchSectionAction([courseId, sectionId, contentTypes], sectionId)
    );
  };
};

export const fetchSectionIfNotFetched =
  (courseId: string, sectionId: string, contentTypes?: Array<ContentType>) =>
  async (dispatch: Dispatch, getState: GetState) => {
    if (sectionNeedsFetching(getState(), sectionId)) {
      return dispatch(fetchSection(courseId, sectionId, contentTypes));
    }
  };

export const fetchSectionAndEnrolIfNeeded = (
  courseId: string,
  sectionId: string,
  contentTypes?: Array<ContentType>
) => {
  return async (dispatch: Dispatch, getState: GetState) => {
    const state = getState();

    if (isSignedIn(state)) {
      await dispatch(fetchEnrolledCoursesIfNotFetched());
    }

    const section = await dispatch(
      fetchSectionIfNotFetched(courseId, sectionId, contentTypes)
    );

    if (section && !(section instanceof Error)) {
      dispatch(saveNewCourseEnrollmentLocallyIfNotAlreadyEnrolled(courseId));
    }

    return section;
  };
};

export const fetchSectionIfNotFetchedAndEnrolIfNeeded = (
  courseId: string,
  sectionId: string,
  contentTypes?: Array<ContentType>
) => {
  return (dispatch: Dispatch, getState: GetState) => {
    if (sectionNeedsFetching(getState(), sectionId)) {
      return dispatch(
        fetchSectionAndEnrolIfNeeded(courseId, sectionId, contentTypes)
      );
    }
  };
};

export type QueryCourseResponseType = {
  results: {
    hits: Array<{
      _score: number;
      _source: CourseEntityIndexViewObject;
      matchedTerms: string[];
    }>;
  };
};

export async function queryInCourse({
  courseId,
  term
}: {
  courseId: string;
  term: string;
}) {
  return getCourseServiceClient().queryInCourse({
    types: [
      COURSE_ENTITY_INDEX_TYPES.section,
      COURSE_ENTITY_INDEX_TYPES.module,
      COURSE_ENTITY_INDEX_TYPES.examQuestion
    ],
    query: term,
    courseId,
    highlight: true,
    size: 20
  });
}

export async function fetchCourseWords({
  courseId,
  lastKey,
  limit = 1000
}: {
  courseId: string;
  lastKey?: string;
  limit?: number;
}) {
  const response = await getCourseServiceClient().fetchCourseWords(courseId, {
    lastKey,
    limit
  });
  return response?.items || [];
}

export async function fetchWords({ wordIds }: { wordIds: string[] }) {
  const response = await getCourseServiceClient().fetchWords(wordIds);
  return response?.items || [];
}

export async function fetchWord({ wordId }: { wordId: string }) {
  const response = await getCourseServiceClient().fetchWord(wordId);

  return response;
}
